// „Възстанови" на анулирано движение — с потвърждение; ползват го модалът на
// документа и менюто „⋯" на реда в „Анулирани дейности". Възстановяването
// връща записа в нормалните списъци и прилага количествата наново.
// Въпросът (components/confirm) назовава документа („доставка №123"), показва
// количествата, които се прилагат отново, и тече с въртящ се бутон до отговора.
import { createElement } from "react";
import { UndoOutlined } from "@ant-design/icons";
import { App } from "antd";
import { useTranslation } from "react-i18next";
import { useRestoreDeletedMovement } from "../../../../hooks/useInventory";
import type { DeletedMovement } from "../../../../api/inventory";
import { useConfirm } from "../../components/confirm/useConfirm";
import { productLineItems, useProductById } from "../../components/confirm/confirmPreviews";

const KNOWN_TYPES = ["delivery", "writeoff", "scrap", "stocktake", "transfer", "substitution", "report"];

export const useRestoreDeleted = () => {
  const { t } = useTranslation();
  // React 19: статичните antd методи не работят — ползваме контекста от <App>
  const { message } = App.useApp();
  const confirm = useConfirm();
  const productById = useProductById();
  const restoreMutation = useRestoreDeletedMovement();

  const requestRestore = (movement: DeletedMovement, onRestored?: () => void) => {
    const known = KNOWN_TYPES.includes(movement.type);
    const number = movement.invoiceNumber || movement.id;
    const signed = movement.type === "stocktake";
    void confirm({
      intent: "info",
      icon: createElement(UndoOutlined),
      title: t("inventoryPage.cfmRestoreTitle", {
        document: known
          ? t(`inventoryPage.cfmRestoreDoc_${movement.type}`, { number })
          : t("inventoryPage.cfmRestoreDocOther", { number }),
      }),
      explanation: t("inventoryPage.deletedRestoreBody"),
      preview: {
        caption: t("inventoryPage.cfmRestoreCaption"),
        // инвентаризацията прилага разликите си (±), останалите — количествата
        items: productLineItems(
          movement.lines.map((line) => ({
            ...line,
            quantity: signed ? (line.diff ?? line.quantity) : line.quantity,
          })),
          productById,
          { signed },
        ),
        total: movement.lines.length,
      },
      note: { kind: "undo", text: t("inventoryPage.cfmRestoreUndo") },
      confirmLabel: known
        ? t(`inventoryPage.cfmRestoreVerb_${movement.type}`)
        : t("inventoryPage.cfmRestoreVerbOther"),
      action: () =>
        new Promise<void>((resolve) => {
          restoreMutation.mutate(movement.id, {
            onSuccess: () => {
              message.success(t("inventoryPage.deletedRestored"));
              resolve();
              onRestored?.();
            },
            onError: (e: unknown) => {
              const err = e as { response?: { data?: { detail?: string } } };
              message.error(err.response?.data?.detail || t("inventoryPage.errRestore"));
              resolve();
            },
          });
        }),
    });
  };

  return { requestRestore, isRestoring: restoreMutation.isPending };
};

export default useRestoreDeleted;
